/**
 * A base modal for flagging purposes that accepts a list of "actions" which
 * the user can select from.
 */
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {Id, View} from "@khanacademy/wonder-blocks-core";
import {Choice, RadioGroup, TextArea} from "@khanacademy/wonder-blocks-form";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body, LabelMedium} from "@khanacademy/wonder-blocks-typography";

import type {Action} from "./types.ts";

type Props = {
    actions: Array<Action>;
    bodyText?: React.ReactNode;
    // A function that will close the modal once called
    closeModal: () => unknown;
    onSelectReason: (type: string, justification: string) => unknown;
    textContent: string;
    // Used for fixture testing
    inFixture?: boolean;
};

type State = {
    justification: string;
    reason: string;
};

class BaseFlagModal extends React.Component<Props, State> {
    static defaultProps: {
        textContent: string;
    } = {
        textContent: "",
    };

    state: State = {
        justification: "",
        reason: "",
    };

    handleChange: (reason: string) => void = (reason: string) => {
        this.setState({reason});
    };

    handleSubmit: () => void = () => {
        this.props.onSelectReason(this.state.reason, this.state.justification);
        this.props.closeModal();
    };

    handleJustificationChange: (value: string) => void = (value) => {
        this.setState({justification: value});
    };

    renderAction: (action: Action, key: number) => React.ReactElement<any> = (
        action: Action,
        key: number,
    ) => {
        const {examples, header, name} = action;
        const desc = examples.join(", ");
        return (
            <Choice key={key} label={header} value={name} description={desc} />
        );
    };

    renderButtons: () => React.ReactNode = () => {
        const {actions} = this.props;
        const items = actions.map((action, index) =>
            this.renderAction(action, index),
        );
        return (
            <RadioGroup
                label={t`This is instance of:`}
                groupName="reason"
                onChange={this.handleChange}
                selectedValue={this.state.reason}
            >
                {items}
            </RadioGroup>
        );
    };

    render(): React.ReactNode {
        const {bodyText} = this.props;

        const MAX_LENGTH = 275;
        let text = this.props.textContent;
        if (text.length > MAX_LENGTH) {
            text = text.substring(0, MAX_LENGTH) + "...";
        }

        return (
            <Id>
                {(idRoot) => {
                    const labelId = `${idRoot}label`;
                    const textAreaId = `${idRoot}textarea`;
                    return (
                        <OnePaneDialog
                            title={t`Flag for Guardian attention`}
                            content={
                                <View>
                                    <Body>{bodyText}</Body>
                                    <Strut size={spacing.medium_16} />
                                    <View>
                                        <LabelMedium
                                            id={labelId}
                                            htmlFor={textAreaId}
                                            tag="label"
                                        >
                                            {t`How does this violate our guidelines?`}
                                        </LabelMedium>
                                        <TextArea
                                            id={textAreaId}
                                            style={styles.textarea}
                                            rows={3}
                                            value={this.state.justification}
                                            onChange={
                                                this.handleJustificationChange
                                            }
                                            resizeType="vertical"
                                        />
                                    </View>
                                    <Strut size={spacing.medium_16} />
                                    {this.renderButtons()}
                                </View>
                            }
                            footer={
                                <View style={styles.modalFooter}>
                                    <Button
                                        kind="tertiary"
                                        onClick={this.props.closeModal}
                                    >
                                        {t`Cancel`}
                                    </Button>
                                    <Strut size={spacing.medium_16} />
                                    <Button
                                        kind="primary"
                                        onClick={this.handleSubmit}
                                        disabled={!this.state.reason}
                                    >
                                        {t`Submit`}
                                    </Button>
                                </View>
                            }
                        />
                    );
                }}
            </Id>
        );
    }
}

const styles = StyleSheet.create({
    textarea: {
        width: "100%",
        marginTop: spacing.small_12,
    },
    modalFooter: {
        flexDirection: "row",
        justifyContent: "flex-end",
    },
});

export default BaseFlagModal;
